/**
 * CSS-class hint filter for substrate-v2 capture.
 *
 * The substrate carries `~css=tag.cls.cls` hints so `buildLocator`'s
 * `cssHint` predicate has a fallback when role+name aren't unique. Hashy
 * generated classes (CSS-in-JS, CSS-module hashes) are useless: they change
 * on every build and can't be templated. Strip them.
 *
 * Per `docs/api/services/snapshot-substrate.md` (Capture Details), drop classes matching:
 *   - `/_[a-z0-9]{5,}$/`               — CSS-module suffix hash, e.g. `Button_a8c3d`
 *   - `/^[A-Z][a-zA-Z]+_[a-zA-Z0-9]+$/` — webpack-style component hash, e.g. `Button_primary`
 *
 * Also reuse the existing v1 `extractSemanticHint` ruleset (CSS-in-JS prefixes,
 * Bubble.io framework names, Tailwind utilities) so v1 and v2 agree.
 *
 * Returns the surviving class list (preserving order). Caller composes the
 * `~css` value as `tag.cls.cls` (or `~css-class=cls cls` when no tag is known).
 */
export function filterCssClasses(classes: readonly string[]): string[] {
  return classes.filter((cls) => !cls || keepClass(cls));
}

const SPEC_HASH_SUFFIX = /_[a-z0-9]{5,}$/;
const SPEC_COMPONENT_HASH = /^[A-Z][a-zA-Z]+_[a-zA-Z0-9]+$/;
const CSS_IN_JS_PREFIX = /^(css|sc|emotion|styled|svelte)-/;
const BUBBLE_PREFIX = /^(clickable-element|bubble-element|bubble-r-container|bubble-r-vertical-center)/;
const CSS_MODULE_HASH = /^_[a-zA-Z0-9]{4,}$/;
const TAILWIND_PREFIXED = /^(p|m|w|h|gap|text|bg|border|rounded|shadow|opacity|z|flex|grid|block|inline|hidden|relative|absolute|fixed|sticky|overflow|cursor|transition|ring|outline|sr|not|group|peer|dark|hover|focus|active|disabled|data|aria)-/;
const TAILWIND_COMPOUND = /^(items|justify|self|place|space|divide|whitespace|break|font|leading|tracking|decoration|underline|overline|line|list|placeholder|align|content|object|max|min|inset|top|right|bottom|left|order|auto|grow|shrink|basis|table|caption|scale|rotate|translate|skew|origin|select|snap|scroll|touch|resize|appearance|columns|aspect|accent|caret|fill|stroke|will|animate|delay|duration|ease)-./;
const TAILWIND_BARE = /^(flex|grid|block|inline|hidden|table|contents)$/;

function keepClass(cls: string): boolean {
  if (SPEC_HASH_SUFFIX.test(cls)) return false;
  if (SPEC_COMPONENT_HASH.test(cls)) return false;
  if (CSS_IN_JS_PREFIX.test(cls)) return false;
  if (BUBBLE_PREFIX.test(cls)) return false;
  if (CSS_MODULE_HASH.test(cls)) return false;
  if (TAILWIND_PREFIXED.test(cls)) return false;
  if (TAILWIND_COMPOUND.test(cls)) return false;
  if (TAILWIND_BARE.test(cls)) return false;
  return true;
}

/**
 * Build a substrate-v2 `~css` value from a tag and class list.
 * Format: `tag.cls.cls`. Returns `null` when no surviving class remains and
 * tag alone is too generic to be useful (`div`, `span`, `a`, `button`).
 *
 * The locator builder treats `tag` as a fallback selector segment; emitting
 * `~css=div` adds noise without disambiguation, so we suppress it.
 */
export function buildCssHint(
  tag: string | null | undefined,
  classes: readonly string[]
): string | null {
  const kept = filterCssClasses(classes);
  if (!tag && kept.length === 0) return null;
  if (kept.length === 0 && tag && GENERIC_TAGS.has(tag.toLowerCase())) return null;
  // Format: `tag.cls.cls`. When no tag, leading dot keeps the parser's
  // first split slot empty so it doesn't misread the first class as a tag.
  const tagPart = tag ? tag.toLowerCase() : '';
  if (kept.length === 0) return tagPart || null;
  return tagPart ? `${tagPart}.${kept.join('.')}` : `.${kept.join('.')}`;
}

const GENERIC_TAGS = new Set(['div', 'span', 'a', 'button', 'p', 'i', 'b']);
